import sampleOfferImage from '~shared-assets/images/sample-offer.png';

import { CSSProperties } from 'react';
import styled from 'styled-components';

import { NorlysFont } from '~src/branding';
import { TextInputNorlys } from '~src/components/BaseInput';
import { BulletPointsNorlys } from '~src/components/BulletPoints';
import { EmailInputNorlys } from '~src/components/EmailInput';
import { PhoneNumberInputNorlys } from '~src/components/PhoneNumberInput';
import { LabelSwitchNorlys } from '~src/components/Switch';
import { TextNorlys } from '~src/components/Text';
import { useCustomerContext } from '~src/context/CustomerContext';
import { useDisplayContext } from '~src/context/DisplayContext';
import { CustomerDetails } from '~src/model';

import { useSwitchEntries } from './common';
import { CALCULATION_BULLET_POINTS_ENTRIES_NORLYS, CALCULATION_BULLET_POINTS_TITLE } from './constants';
import { useGdprConsent } from './useGdprConsent';

const Section = styled.div({ ...NorlysFont.style('default', 16) });

type CustomerInformationNorlysProps = {
  isLoading?: boolean;
};

export const CustomerInformationNorlys = ({ isLoading }: CustomerInformationNorlysProps) => {
  const { isMobile } = useDisplayContext();
  const consentText = useGdprConsent('norlys');

  const { customer, setCustomer } = useCustomerContext();
  const updateState = (input: Partial<CustomerDetails>) => setCustomer?.({ ...customer, ...input });

  const {
    firstName: customerFirstName,
    lastName: customerLastName,
    email: customerEmail,
    phoneNumber: customerPhoneNumber,
  } = customer ?? {};

  const inputStyle = { marginTop: isMobile ? '16px' : '8px' };
  const switchEntries = useSwitchEntries({ customer, setCustomer });

  const contentMargin = isMobile ? {} : { margin: '32px 0' };

  const inputFieldsStyle: CSSProperties = isMobile
    ? { margin: '16px 0', width: '100%' }
    : { display: 'flex', flexDirection: 'column', margin: '8px 0' };

  const containerPadding = isMobile ? '24px' : undefined;

  return (
    <div style={{ padding: containerPadding }}>
      <Section>
        Bodils energiberegning giver et overblik over pris, og hvor mange penge du kan spare ved at skifte til en
        varmepumpe. Del dine oplysninger med os, og vi sørger for at levere din personlige beregning.
      </Section>

      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? undefined : 'center',
          ...contentMargin,
        }}
      >
        <div style={inputFieldsStyle}>
          <TextInputNorlys
            {...{
              disabled: isLoading,
              style: inputStyle,
              value: customerFirstName ?? undefined,
              placeholder: 'Hans',
              label: 'Fornavn',
              onChange: firstName => updateState({ firstName }),
            }}
          />

          <TextInputNorlys
            {...{
              disabled: isLoading,
              style: inputStyle,
              value: customerLastName ?? undefined,
              placeholder: 'Hansen',
              label: 'Efternavn',
              onChange: lastName => updateState({ lastName }),
            }}
          />

          <EmailInputNorlys
            {...{
              disabled: isLoading,
              style: inputStyle,
              value: customerEmail ?? undefined,
              onChange: email => updateState({ email }),
            }}
          />

          <PhoneNumberInputNorlys
            {...{
              disabled: isLoading,
              style: inputStyle,
              value: customerPhoneNumber ?? undefined,
              label: 'Telefonnummer',
              onChange: phoneNumber => updateState({ phoneNumber }),
            }}
          />
        </div>

        {!isMobile && (
          <>
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundImage: `url("${sampleOfferImage}")`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />

            <BulletPointsNorlys
              title={CALCULATION_BULLET_POINTS_TITLE}
              entries={CALCULATION_BULLET_POINTS_ENTRIES_NORLYS}
            />
          </>
        )}
      </div>

      <TextNorlys>{consentText}</TextNorlys>

      <div style={{ margin: '24px 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {switchEntries.map(value => (
          <LabelSwitchNorlys disabled={isLoading} {...value} key={value.key} />
        ))}
      </div>
    </div>
  );
};
